import { tWithKeyAttr as t } from "../../../i18nHelper";
import React from "react";
import styles from "./updateFormModal.module.css";
import { IAdminMessage } from "../../../services/types/utils.types";

interface UpdateFormModalProps {
  form: Partial<IAdminMessage>;
  editId: number | null;
  isSaving: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDescChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onClose: () => void;
  onOpenUserSelection: () => void;
  selectedUsers: { id: number; name: string }[];
  onRemoveUser: (id: number) => void;
  sendSms: boolean;
  sendEmail: boolean;
  onToggleSms: () => void;
  onToggleEmail: () => void;
}

function UpdateFormModal({
  form,
  editId,
  isSaving,
  onSubmit,
  onChange,
  onDescChange,
  onClose,
  onOpenUserSelection,
  selectedUsers,
  onRemoveUser,
  sendSms,
  sendEmail,
  onToggleSms,
  onToggleEmail,
}: UpdateFormModalProps) {
  return (
    <form className={styles.popup} onSubmit={onSubmit}>
      <h2 className={styles.title}>
        {editId ? t("EDIT_UPDATE") : t("ADD_UPDATE")}
      </h2>
      <input
        className={styles.input}
        type="text"
        name="Title"
        placeholder={t("UPDATE_TITLE")}
        value={form.Title ?? ""}
        onChange={onChange}
        autoFocus
      />
      <textarea
        className={styles.textarea}
        name="desc"
        placeholder={t("UPDATE_DESCRIPTION")}
        value={form.Desc ?? ""}
        onChange={onDescChange}
        rows={5}
      />
      <div className={styles.hints}>
        <p className={styles.hint}>{t("LINK_FORMAT_HINT")}</p>
        <p className={styles.hint}>{t("LINK_USER_HINT")}</p>
      </div>
      
      {/* User Selection Section */}
      <div className={styles.userSelection}>
        <label className={styles.userLabel}>{t("LINK_TO_SPECIFIC_USER")}</label>
        {selectedUsers.length > 0 ? (
          <div className={styles.badgesContainer}>
            {selectedUsers.map((u) => (
              <div key={u.id} className={styles.selectedUserBadge}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>{u.name}</span>
                <button
                  type="button"
                  className={styles.removeBadgeBtn}
                  onClick={() => onRemoveUser(u.id)}
                  title={t("REMOVE")}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <span className={styles.noUserSelected}>{t("NO_USER_SELECTED")}</span>
        )}
        <button
          type="button"
          className={styles.selectUserBtn}
          onClick={onOpenUserSelection}
        >
          {selectedUsers.length > 0 ? t("CHANGE_USER") : t("SELECT_USER")}
        </button>
      </div>

      {/* SMS / Email Notification Checkboxes.
          Disabled until at least one user is selected — SMS/email are only sent
          via the bulk endpoint with explicit recipients, so toggling them with no
          users selected does nothing (silent no-op). */}
      {(() => {
        // Hidden while editing: notifications go out when the update is created, and the
        // endpoint that handles edits carries no notification fields at all. Leaving the
        // checkboxes visible here is what routes an edit to the create-only bulk endpoint
        // and produces a duplicate update instead of an edit.
        if (editId !== null) return null;

        const notificationsDisabled = selectedUsers.length === 0;
        return (
          <div className={styles.notificationSection}>
            <label className={styles.userLabel}>{t("SEND_NOTIFICATIONS")}</label>
            <div
              className={`${styles.checkboxRow} ${
                notificationsDisabled ? styles.checkboxRowDisabled : ""
              }`}
              title={notificationsDisabled ? t("SELECT_USER") : undefined}
            >
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={sendSms}
                  onChange={onToggleSms}
                  disabled={notificationsDisabled}
                  className={styles.checkboxInput}
                />
                {t("SEND_SMS")}
              </label>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={onToggleEmail}
                  disabled={notificationsDisabled}
                  className={styles.checkboxInput}
                />
                {t("SEND_EMAIL")}
              </label>
            </div>
            {notificationsDisabled && (
              <p className={styles.hint}>{t("NOTIFICATIONS_NEED_RECIPIENTS")}</p>
            )}
          </div>
        );
      })()}

      <div className={styles.btnGroup}>
        <button
          type="submit"
          className={`button ${styles.submitBtn} hover`}
          disabled={(!form.Title && !form.Desc) || isSaving}
        >
          {isSaving ? t("LOADING_LABEL") : editId ? t("UPDATE_1") : t("SEND")}
        </button>
        <button
          type="button"
          className={`button ${styles.cancelBtn} hover`}
          onClick={onClose}
        >
          {t("BATAL")}
        </button>
      </div>
    </form>
  );
}

export default UpdateFormModal;
